import { EngagementChannelType } from '@/__generated__/graphql';

import type { ViewerRole } from '../components/EngagementHub/roles';
import { visibleDirectChannelTypes } from '../components/EngagementHub/roles';
import type { ChannelView } from './viewModel';

export const TRILATERAL_LABEL = 'Engagement chat';

/**
 * The pill label, which depends on the VIEWER and not only on the channel.
 *
 * `Everyone` / `OnFrontiers & client` were written from OnFrontiers' side and
 * shown to the client, who read themselves described in the third person
 * (ENG-5732). The prototype's fix is to name the channel relative to whoever is
 * reading it: a party sees exactly one bilateral channel — the wall guarantees
 * it (`visibleDirectChannelTypes`) — so `Direct with OnFrontiers` is
 * unambiguous for the customer AND the expert, one string for both.
 *
 * OF staff are the one viewer who sees both, so they are the one viewer who
 * needs them told apart.
 */
export function channelLabel(type: EngagementChannelType, viewerRole: ViewerRole): string {
  if (type === EngagementChannelType.Trilateral) return TRILATERAL_LABEL;
  if (viewerRole === 'of_fulfillment') {
    return type === EngagementChannelType.OfClient ? 'Direct · Client' : 'Direct · Expert';
  }
  return 'Direct with OnFrontiers';
}

/**
 * Who is in the channel, stated in the UI rather than left to the label.
 *
 * Written from nobody's point of view on purpose. "Only you and OnFrontiers"
 * would need a different sentence per viewer, and the one thing this copy must
 * never get wrong is which side is excluded (§6.3): an expert and a customer on
 * the same engagement must never see each other's channel with OF, and the
 * label is where a reader checks that before typing.
 */
export const CHANNEL_PRIVACY: Record<EngagementChannelType, string> = {
  [EngagementChannelType.Trilateral]:
    'Everyone on this engagement can read this: the customer side, the expert, and OnFrontiers.',
  [EngagementChannelType.OfClient]:
    'The customer side and OnFrontiers only. The expert is not in this channel.',
  [EngagementChannelType.OfExpert]:
    'The expert and OnFrontiers only. The customer side is not in this channel.',
};

/** `roles.ts`'s string union → the generated enum the wire uses. */
function toChannelType(direct: 'of_client' | 'of_expert'): EngagementChannelType {
  return direct === 'of_client' ? EngagementChannelType.OfClient : EngagementChannelType.OfExpert;
}

/**
 * Which channels this viewer can select.
 *
 * The **server** is the authority: an engagement's `messageChannels` are already
 * wall-filtered, so a channel the viewer may not see is simply absent — this
 * function never adds one back. `visibleDirectChannelTypes` survives only as the
 * display *order* for the bilateral channels (§9.8), not as a permission check.
 *
 * The one addition is trilateral: channels are lazily created on first message,
 * so a brand-new engagement returns an empty list while the trilateral thread is
 * still the correct place to send. Offering it is what creates it.
 */
export function selectableChannels(
  channels: ChannelView[],
  viewerRole: ViewerRole
): EngagementChannelType[] {
  const present = new Set(channels.map((c) => c.channelType));
  const order: EngagementChannelType[] = [
    EngagementChannelType.Trilateral,
    ...visibleDirectChannelTypes(viewerRole).map(toChannelType),
  ];
  return order.filter((t) => t === EngagementChannelType.Trilateral || present.has(t));
}

/**
 * The bilateral channels this viewer could open but has not (ENG-5452 / FE6).
 *
 * The trilateral thread needs no affordance — sending to it is what creates it,
 * and `selectableChannels` always offers it. A bilateral channel cannot work
 * that way: `sendEngagementMessage` answers `channel_not_found` for one that
 * does not exist yet, so opening it is a deliberate act with its own mutation.
 *
 * OF staff are excluded even though `visibleDirectChannelTypes` grants them
 * both. Staff read every channel by role and it is the SEND that records them
 * (ENG-5677), so there is nothing for staff to open; and this surface must offer
 * staff no call to action at all (ENG-5683).
 */
export function openableDirectChannels(
  channels: ChannelView[],
  viewerRole: ViewerRole
): EngagementChannelType[] {
  if (viewerRole === 'of_fulfillment') return [];
  const present = new Set(channels.map((c) => c.channelType));
  return visibleDirectChannelTypes(viewerRole)
    .map(toChannelType)
    .filter((t) => !present.has(t));
}
